import React, { FunctionComponent, useMemo } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import logo from '../../assets/upstart-white.png';
import logoutImage from '../../assets/logout.png';
import checkImage from '../../assets/check.png';
import { useAppDispatch } from '../../redux/store';
import { Button } from '../Input/Button/Button';
import { LinkButton } from '../Input/Button/LinkButton';
import { ROUTES } from '../../utils/routes';
import { DealerLaunchSection, DealerLaunchStep } from './types';
import { getStepCompletePercent, getActiveDealerLaunchSections } from './steps';
import { Dealer } from '../../types/dealerTypes';
import { logout } from '../../utils/redux';
import styles from './styles.module.scss';
import { userHasLoggedOut } from '../../utils/session-timeout';

interface Props {
  dealer: Dealer;
  currentSection?: DealerLaunchSection;
  hideSectionNav?: DealerLaunchStep['hideSectionNav'];
  hideHomeNav?: DealerLaunchStep['hideHomeNav'];
  hideReviewNav?: DealerLaunchStep['hideReviewNav'];
  showAdminView?: boolean;
}

export const DealerLaunchSidebar: FunctionComponent<Props> = ({
  dealer,
  currentSection,
  hideSectionNav,
  hideHomeNav,
  hideReviewNav,
  showAdminView,
}) => {
  const dispatch = useAppDispatch();

  // calculate the progress for all steps completed
  const stepCompletePercent = useMemo(() => getStepCompletePercent(dealer), [
    dealer,
  ]);

  const logoutProcess = () => {
    userHasLoggedOut();
    logout(dispatch);

    // remove the session key generated by admin access dealer step
    sessionStorage.removeItem('adminAccessDealer');
  };

  const hideSectionNavBool =
    typeof hideSectionNav === 'boolean'
      ? hideSectionNav
      : hideSectionNav?.(dealer);
  const hideHomeNavBool =
    typeof hideHomeNav === 'boolean' ? hideHomeNav : hideHomeNav?.(dealer);
  const hideReviewNavBool =
    typeof hideReviewNav === 'boolean'
      ? hideReviewNav
      : hideReviewNav?.(dealer);

  return (
    <div className={styles.sidebarContainer}>
      <div className={styles.sidebar}>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <Link
          className={styles.header}
          to={hideHomeNavBool ? '#' : ROUTES.DEALER_DASHBOARD_HOME.getPath()}
        >
          <img src={logo} alt="Logo" />
          Launch Pad
        </Link>

        {!hideHomeNavBool && (
          <LinkButton
            className={styles.sidebarButton}
            label="Home"
            to={ROUTES.DEALER_DASHBOARD_HOME.getPath()}
          />
        )}

        {!hideSectionNavBool && (
          <div className={styles.sidebarSectionNav}>
            {getActiveDealerLaunchSections(dealer).map((section) => (
              <LinkButton
                key={section.route.template}
                className={classNames(
                  styles.sidebarButton,
                  section === currentSection && 'active',
                )}
                activeClassName=" "
                to={section.route.getPath(dealer)}
              >
                <div
                  className={classNames(
                    styles.sectionStatus,
                    section.isComplete(dealer) && styles.sectionStatusComplete,
                  )}
                >
                  <img src={checkImage} alt="Complete" />
                </div>
                <div className={styles.sectionLink} />
                {section.name}
              </LinkButton>
            ))}
          </div>
        )}

        {!hideReviewNavBool && (
          <LinkButton
            className={styles.sidebarButton}
            label="Review"
            to={ROUTES.DEALER_DASHBOARD_REVIEW.getPath()}
          />
        )}

        <div className="spacer" />
        <div className={styles.sidebarProgress}>
          <div className={styles.sidebarProgressDescription}>
            <div>Set Up Progess</div>
            <div className="spacer" />
            <div>{`${(stepCompletePercent * 100).toFixed(0)}% Complete`}</div>
          </div>
          <div className={styles.progressBar}>
            <div
              className={styles.progressBarFill}
              style={{ width: `${stepCompletePercent * 100}%` }}
            />
          </div>
        </div>
        {showAdminView && (
          <div className={styles.adminBanner}>
            You are a{' '}
            <span className={styles.adminBannerDealerName}>UAR Admin</span>{' '}
            logged in as dealer{' '}
            <span className={styles.adminBannerDealerName}>{dealer.name}</span>
          </div>
        )}
        <Button
          className={classNames(styles.sidebarButton, styles.logoutButton)}
          onClick={logoutProcess}
        >
          <img src={logoutImage} alt="Log Out" />
          Log Out
        </Button>
      </div>
    </div>
  );
};
